/**
 * Acquisition-offer eligibility check.
 *
 * Used by promo banners / discount CTAs to decide whether to surface an
 * offer (ADV50, ADV30Y, IR50, tryai) to the current user. The same check
 * is enforced server-side at promo-code application time — this client
 * call is purely a UX optimisation: hide the CTA up-front rather than
 * surface it and then reject the redemption.
 *
 * Eligibility window is 6 months (server-controlled). The endpoint
 * fails open — when the Stripe Parameter Store entries are unreachable
 * the backend allows the redemption rather than blocking legitimate
 * new customers. So a `true` response is reliable, a `false` response
 * is reliable, and network failures should be treated as "show the
 * offer, let the backend make the call at apply time".
 */

import axios from "axios";

export type AcquisitionOfferKey = "adv50" | "adv30y" | "ir50" | "tryai";

export type EligibilityReason =
    | "eligible"
    | "already_redeemed"
    | "anonymous_user"
    | "no_coupon_ids"
    | "unknown_offer";

export interface EligibilityResponse {
    offer_key: string;
    eligible: boolean;
    reason: EligibilityReason;
    /** ISO datetime if the user redeemed inside the window, else null. */
    last_redeemed_at: string | null;
}

/**
 * Hit the backend eligibility endpoint. Returns the parsed JSON.
 *
 * On network failure we throw — the caller (typically a hook) decides
 * whether to treat that as "show the CTA" (fail open) or "hide it"
 * (fail closed). Most callers should fail open: the server-side gate
 * catches the redemption anyway, and hiding a legitimate offer because
 * of a flaky network is the worse outcome.
 */
export const checkOfferEligibility = async (
    offerKey: AcquisitionOfferKey
): Promise<EligibilityResponse> => {
    const res = await axios.get<EligibilityResponse>(
        `/API/v2/offer-eligibility?offer=${encodeURIComponent(offerKey)}`
    );
    return res.data;
};
